import React from "react";
import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "../hooks/useCalendar";
import {
  TrashIcon,
  EditIcon,
  LinkIcon,
  CloseIcon,
  PlusIcon,
  SpinnerIcon,
} from "@/components/icons";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { OptionDropdown, DropdownOption } from "@/components/ui/OptionDropdown";

export const CATEGORY_COLORS: Record<string, string> = {
  holiday: "bg-red-50 border-red-200 text-red-800",
  cultural: "bg-purple-50 border-purple-200 text-purple-800",
  seasonal: "bg-amber-50 border-amber-200 text-amber-800",
  industry: "bg-blue-50 border-blue-200 text-blue-800",
  local: "bg-green-50 border-green-200 text-green-800",
  awareness: "bg-pink-50 border-pink-200 text-pink-800",
  commercial: "bg-orange-50 border-orange-200 text-orange-800",
  religious: "bg-indigo-50 border-indigo-200 text-indigo-800",
  sector: "bg-teal-50 border-teal-200 text-teal-800",
};

const STATUS_OPTIONS: DropdownOption[] = [
  { value: "upcoming", label: "Upcoming", bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-400" },
  { value: "planned", label: "Planned", bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  { value: "posted", label: "Posted", bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  { value: "dismissed", label: "Dismissed", bg: "bg-gray-100", text: "text-gray-400", dot: "bg-gray-300" },
];

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function formatMonthDay(
  month: number | null,
  day: number | null
): string | null {
  if (month === null || day === null) return null;
  return `${MONTH_NAMES[month - 1]} ${day}`;
}

export function formatDateRange(event: {
  eventMonth: number | null;
  eventDay: number | null;
  endMonth: number | null;
  endDay: number | null;
}): string | null {
  const start = formatMonthDay(event.eventMonth, event.eventDay);
  if (!start) return null;
  const end = formatMonthDay(event.endMonth, event.endDay);
  if (!end) return start;
  if (event.eventMonth === event.endMonth) {
    return `${start}-${event.endDay}`;
  }
  return `${start} - ${end}`;
}

export function daysUntilNext(month: number, day: number): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  let target = new Date(now.getFullYear(), month - 1, day);
  if (target.getTime() < now.getTime()) {
    target = new Date(now.getFullYear() + 1, month - 1, day);
  }

  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function nextOccurrenceYear(
  month: number | null,
  day: number | null,
  now: Date = new Date()
): number {
  if (month === null || day === null) return now.getFullYear();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(now.getFullYear(), month - 1, day);
  return target.getTime() < today.getTime()
    ? now.getFullYear() + 1
    : now.getFullYear();
}

export function eventHasPostLink(event: CalendarEvent): boolean {
  return event.links.some((link) => link.targetType === "post");
}

interface CommonProps {
  event: CalendarEvent;
  variant?: "default" | "compact";
  className?: string;
}

interface DefaultProps extends CommonProps {
  variant?: "default";
  onUpdate?: (
    id: string,
    data: { status?: string; event_month?: number | null; event_day?: number | null }
  ) => void;
  onEdit?: (event: CalendarEvent) => void;
  onLink?: (event: CalendarEvent) => void;
  onUnlink?: (linkId: string) => void;
  onCreateIdea?: (event: CalendarEvent) => void;
  creatingIdea?: boolean;
}

interface CompactProps extends CommonProps {
  variant: "compact";
  /** When provided, renders a Pin button. Hidden if event already has a post link. */
  onPin?: (event: CalendarEvent) => void;
  /** When provided, renders a small × on hover. */
  onUnpin?: (event: CalendarEvent) => void;
}

type CalendarEventCardProps = DefaultProps | CompactProps;

const LINK_TYPE_LABELS: Record<string, string> = {
  post: "Post",
  idea: "Idea",
  brief: "Brief",
};

export const CalendarEventCard: React.FC<CalendarEventCardProps> = (props) => {
  const { event, className = "" } = props;
  const navigate = useNavigate();
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.home.label);
  const IdeaIcon = ENTITY_CONFIG.idea.icon;
  const variant = props.variant ?? "default";
  const hasDate = event.eventMonth !== null && event.eventDay !== null;
  const dateLabel = formatDateRange(event);
  const days = hasDate
    ? daysUntilNext(event.eventMonth!, event.eventDay!)
    : null;
  const categoryStyle =
    CATEGORY_COLORS[event.category] ||
    "bg-gray-50 border-gray-200 text-gray-800";
  const isDismissed = event.status === "dismissed";
  const hasPost = eventHasPostLink(event);

  if (variant === "compact") {
    const compact = props as CompactProps;
    return (
      <div
        className={`group/event flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs ${categoryStyle} ${className}`}
      >
        {dateLabel && (
          <span className="font-medium shrink-0">
            {dateLabel}
            {days !== null && days >= 0 && days <= 7 && (
              <span className="ml-1 px-1 py-0 rounded-sm bg-white/60 text-[10px]">
                {days === 0 ? "Today" : days === 1 ? "1d" : `${days}d`}
              </span>
            )}
          </span>
        )}
        <span className="font-semibold truncate flex-1 min-w-0">
          {event.eventName}
        </span>
        {hasPost && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white/70 text-current/80 shrink-0">
            Posted
          </span>
        )}
        {compact.onPin && !hasPost && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              compact.onPin?.(event);
            }}
            className="p-0.5 rounded-sm hover:bg-white/60 transition-colors opacity-60 hover:opacity-100 shrink-0"
            title="Pin to brief"
          >
            <PlusIcon className="w-3 h-3" />
          </button>
        )}
        {compact.onUnpin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              compact.onUnpin?.(event);
            }}
            className="p-0.5 rounded-sm hover:bg-white/60 transition-colors opacity-0 group-hover/event:opacity-60 hover:!opacity-100 shrink-0"
            title="Unpin from brief"
          >
            <CloseIcon className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  const def = props as DefaultProps;

  return (
    <div
      data-testid="calendar-event-row"
      className={`rounded-lg px-3 py-2.5 ${
        isDismissed ? "opacity-50" : ""
      } ${categoryStyle} ${className}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {dateLabel ? (
              <span className="text-sm font-medium">
                {dateLabel}
                {event.movable && event.dateYear && (
                  <span className="ml-1 text-xs opacity-60">{event.dateYear}</span>
                )}
                {event.movable && (
                  <span
                    className="ml-0.5 text-xs text-amber-600"
                    title="Date changes yearly"
                  >
                    ~
                  </span>
                )}
              </span>
            ) : (
              def.onEdit && (
                <button
                  onClick={() => def.onEdit?.(event)}
                  className="text-sm font-medium px-2 py-0.5 rounded-sm border-2 border-dashed border-current/40 hover:border-current/60 bg-white/40 transition-colors"
                >
                  Set date
                </button>
              )
            )}
            {days !== null && days >= 0 && days <= 7 && (
              <span className="text-xs px-1.5 py-0.5 rounded-sm bg-white/60 font-medium">
                {days === 0
                  ? "Today"
                  : days === 1
                  ? "Tomorrow"
                  : `${days}d`}
              </span>
            )}
            <OptionDropdown
              value={event.status}
              options={STATUS_OPTIONS}
              onChange={
                def.onUpdate
                  ? (status) => def.onUpdate?.(event.id, { status })
                  : undefined
              }
            />
          </div>
          <h4 className="font-semibold text-sm leading-snug">
            {event.eventName}
          </h4>
          {/* Clamped so event rows keep the uniform panel rhythm (DD-015);
              the edit modal shows the full angle and relevance. */}
          {event.contentAngle && (
            <p className="text-xs mt-1 opacity-80 line-clamp-2">{event.contentAngle}</p>
          )}
          {event.relevance && (
            <p className="text-xs mt-1 italic opacity-70 line-clamp-1">{event.relevance}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {event.status === "upcoming" && def.onCreateIdea && (
            <button
              onClick={() => def.onCreateIdea?.(event)}
              disabled={def.creatingIdea}
              className="text-xs px-2 py-1 rounded-sm bg-white/80 hover:bg-white border border-current/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
              title="Create idea from this event"
            >
              {def.creatingIdea && <SpinnerIcon className="w-3 h-3" />}
              Create idea
            </button>
          )}
          {def.onLink && (
            <button
              onClick={() => def.onLink?.(event)}
              className="p-1 rounded-sm hover:bg-white/60 transition-colors opacity-60 hover:opacity-100"
              title="Link resource"
            >
              <LinkIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {def.onEdit && (
            <button
              onClick={() => def.onEdit?.(event)}
              className="p-1 rounded-sm hover:bg-white/60 transition-colors opacity-60 hover:opacity-100"
              title="Edit event"
            >
              <EditIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {def.onUpdate && event.status !== "dismissed" && (
            <button
              onClick={() => def.onUpdate?.(event.id, { status: "dismissed" })}
              className="p-1 rounded-sm hover:bg-white/60 transition-colors opacity-60 hover:opacity-100"
              title="Dismiss"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {event.links.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-current/10">
          {event.links.map((link) => (
            <span
              key={link.id}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white/60 border border-current/15"
            >
              {link.targetType === "idea" && link.targetLabel ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/app/${event.clientId}/ideas/${link.targetId}`,
                      trailNavOptions
                    );
                  }}
                  className="inline-flex items-center gap-1 min-w-0 hover:underline"
                  title={link.targetLabel}
                >
                  <IdeaIcon className="w-3 h-3 shrink-0 opacity-60" />
                  <span className="truncate max-w-[10rem] font-medium">
                    {link.targetLabel}
                  </span>
                </button>
              ) : (
                <span className="opacity-60">
                  {LINK_TYPE_LABELS[link.targetType] ?? link.targetType}
                </span>
              )}
              {link.year && <span className="opacity-40">{link.year}</span>}
              {def.onUnlink && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    def.onUnlink?.(link.id);
                  }}
                  className="ml-0.5 p-0.5 rounded-full hover:bg-black/10 transition-colors"
                  title="Unlink"
                >
                  <CloseIcon className="w-2.5 h-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
